import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useSwipeToClose from '../../../hooks/useSwipeToClose';
import Spinner from '../../UI/Spinner';
const complexityOptions = ['simple', 'standard', 'advanced'];

// Approximate budget share per meal type — mirrors UserWizard's MEAL_TYPE_BUDGET_WEIGHTS
const MEAL_TYPE_BUDGET_WEIGHTS = { breakfast: 0.20, lunch: 0.35, dinner: 0.45 };

const CookingStyleDrawer = ({ 
  isOpen, 
  cookingStyle, 
  setCookingStyleAll,
  closeDrawer, 
  onSave,
  isSaving = false,
  budget = null,
  country = 'USA'
}) => {
  const { t } = useTranslation('wizard');
  const { containerRef, dragOffset, isAnimating, shouldRender, isVisible, isDragging, DragHandle } = useSwipeToClose(closeDrawer, isOpen);

  // Temporary local state for draft changes
  const [tempCookingStyle, setTempCookingStyle] = useState(cookingStyle);
  // Tooltip visibility state (for mobile tap)
  const [visibleTooltip, setVisibleTooltip] = useState(null);

  // Reset temp state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTempCookingStyle(cookingStyle);
      setVisibleTooltip(null);
    }
  }, [isOpen, cookingStyle]);

  if (!shouldRender) return null;

  // Handle complexity change for a meal type (local only)
  const handleComplexityChange = (mealType, complexity) => {
    setTempCookingStyle(prev => ({
      ...prev,
      [mealType]: {
        ...prev[mealType],
        cookingTime: complexity
      }
    }));
  };

  // Handle toggle for cooking for 2 days (local only)
  const handleCookFor2DaysToggle = (mealType) => {
    setTempCookingStyle(prev => ({
      ...prev,
      [mealType]: {
        ...prev[mealType],
        cookFor2Days: !prev[mealType]?.cookFor2Days
      }
    }));
  };

  // Save changes to parent and close
  const handleSave = () => {
    // Apply all temp changes to parent at once
    setCookingStyleAll(tempCookingStyle);
    onSave();
  };

  const mealTypes = [
    { key: 'breakfast', label: t('cookingStyle.breakfastStyle'), icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M8.33333 1.6665V3.33317M11.6667 1.6665V3.33317M13.3333 6.6665C13.5543 6.6665 13.7663 6.7543 13.9226 6.91058C14.0789 7.06686 14.1667 7.27882 14.1667 7.49984V14.1665C14.1667 15.0506 13.8155 15.8984 13.1904 16.5235C12.5652 17.1486 11.7174 17.4998 10.8333 17.4998H5.83333C4.94928 17.4998 4.10143 17.1486 3.47631 16.5235C2.85119 15.8984 2.5 15.0506 2.5 14.1665V7.49984C2.5 7.27882 2.5878 7.06686 2.74408 6.91058C2.90036 6.7543 3.11232 6.6665 3.33333 6.6665H15C15.8841 6.6665 16.7319 7.01769 17.357 7.64281C17.9821 8.26794 18.3333 9.11578 18.3333 9.99984C18.3333 10.8839 17.9821 11.7317 17.357 12.3569C16.7319 12.982 15.8841 13.3332 15 13.3332H14.1667M5 1.6665V3.33317" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"></path></svg>
    )},
    { key: 'lunch', label: t('cookingStyle.lunchStyle'), icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><g clipPath="url(#clip0_1408_6195)"><path d="M13.3333 1.65967L11.4166 3.57633C10.9586 4.04366 10.702 4.67195 10.702 5.32633C10.702 5.98072 10.9586 6.60901 11.4166 7.07633L12.9166 8.57634C13.384 9.0344 14.0123 9.29098 14.6666 9.29098C15.321 9.29098 15.9493 9.0344 16.4166 8.57634L18.3333 6.65967M12.5 12.493L2.74997 2.743C2.41737 3.06888 2.15315 3.45785 1.97276 3.88713C1.79238 4.31641 1.69946 4.77736 1.69946 5.243C1.69946 5.70864 1.79238 6.1696 1.97276 6.59888C2.15315 7.02815 2.41737 7.41712 2.74997 7.743L8.83331 13.8263C9.41664 14.4097 10.5 14.4097 11.1666 13.8263L12.5 12.493ZM12.5 12.493L18.3333 18.3263M1.74997 18.1597L7.08331 12.9097M15.8333 4.15967L9.99997 9.993" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"></path></g><defs><clipPath id="clip0_1408_6195"><rect width="20" height="20" fill="white" transform="translate(0 -0.00683594)"></rect></clipPath></defs></svg>
    )},
    { key: 'dinner', label: t('cookingStyle.dinnerStyle'), icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><g clipPath="url(#clip0_1408_6320)"><path d="M10.0001 3.48617V2.65283M10.0001 3.48617C8.45299 3.48617 6.96925 4.10075 5.87529 5.19471C4.78133 6.28867 4.16675 7.7724 4.16675 9.3195M10.0001 3.48617C11.5472 3.48617 13.0309 4.10075 14.1249 5.19471C15.2188 6.28867 15.8334 7.7724 15.8334 9.3195M12.8334 15.4862L15.5001 13.1528C15.6588 12.9996 15.8461 12.8792 16.0513 12.7983C16.2565 12.7175 16.4757 12.6779 16.6962 12.6818C16.9168 12.6857 17.1344 12.7329 17.3366 12.8209C17.5389 12.9089 17.7219 13.0358 17.8751 13.1945C18.0283 13.3532 18.1487 13.5405 18.2296 13.7457C18.3104 13.9509 18.35 14.1701 18.3461 14.3906C18.3423 14.6112 18.295 14.8288 18.207 15.0311C18.119 15.2333 17.9921 15.4163 17.8334 15.5695L14.8334 18.3195C14.2501 18.9862 13.4167 19.3195 12.5001 19.3195H9.16675C8.25008 19.3195 7.41675 18.9862 6.83342 18.3195L5.74842 17.0995C5.6703 17.0115 5.57444 16.9411 5.46715 16.8928C5.35986 16.8446 5.24356 16.8196 5.12592 16.8195H4.16675M1.66675 12.6528H11.6667C12.1088 12.6528 12.5327 12.8284 12.8453 13.141C13.1578 13.4535 13.3334 13.8775 13.3334 14.3195C13.3334 14.7615 13.1578 15.1854 12.8453 15.498C12.5327 15.8106 12.1088 15.9862 11.6667 15.9862H10.0001M3.33341 9.3195H16.6667M4.16675 12.6528V17.6528C4.16675 17.8738 4.07895 18.0858 3.92267 18.2421C3.76639 18.3984 3.55443 18.4862 3.33341 18.4862H1.66675" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"></path></g><defs><clipPath id="clip0_1408_6320"><rect width="20" height="20" fill="white" transform="translate(0 0.986328)"></rect></clipPath></defs></svg>
    )}
  ];

  // Returns formatted extra cost string for a meal type's cookFor2Days toggle, or null
  const getCostHint = (mealType) => {
    if (!budget || typeof budget !== 'object' || !budget.max) return null;
    const weight = MEAL_TYPE_BUDGET_WEIGHTS[mealType] || 0;
    const amount = Math.round(budget.max * weight);
    if (amount <= 0) return null;
    const cur = budget.currency || '$';
    return country === 'USA' ? `+${cur}${amount}` : `+${amount}${cur}`;
  };

  return createPortal(
    <div 
      className={`fixed flex items-end justify-center inset-0 bg-[#0D0C07] z-[200] drawer-overlay`} 
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={closeDrawer}
    >
      <div 
        ref={containerRef}
        className={`flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[90vh]`}
        style={{
          transform: isVisible 
            ? `translateY(${Math.max(0, dragOffset)}px)` 
            : 'translateY(100%)',
          transition: (dragOffset === 0 && isAnimating) || (!isDragging && dragOffset === 0)
            ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
            : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        <DragHandle />
        {/* Close button */}
        <button 
          onClick={closeDrawer}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
          </svg>
        </button>
        
        {/* Scrollable content including header */}
        <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-8 px-6 pb-[116px]">

          <p className="text-[14px] leading-[20px] text-[#65635D] mb-5">
            {t('cookingStyle.subtitle')}
          </p>
          <div className="space-y-6">
            {mealTypes.map(({ key, label, icon }) => {
              const currentComplexity = tempCookingStyle[key]?.cookingTime || 'standard';
              const cookFor2Days = tempCookingStyle[key]?.cookFor2Days || false;
              const costHint = getCostHint(key);

              return (
                <div key={key} className="flex flex-col gap-2 pb-0">
                  {/* Meal type header - styled like main title with icon */}
                  <div className="flex items-center gap-1">
                     {/*<div className="w-6 h-6 flex items-center justify-center">
                      {icon}
                    </div>*/}
                    <h2 className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[16px]
                      font-bold
                      leading-[32px]
                    ">{label}</h2>
                  </div>

                  {/* Complexity selector - 3 columns on all screen sizes */}
                  <div className="grid grid-cols-3 gap-3">
                    {complexityOptions.map((complexity) => {
                      const isSelected = currentComplexity === complexity;
                      
                      return (
                        <button
                          key={complexity}
                          className={`p-3 text-center rounded-[12px] border-[0.5px] border-[#0D0C07] transition-colors ${
                            isSelected
                              ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90' 
                              : 'bg-white md:hover:bg-[#F8F8F7]'
                          }`}
                          onClick={() => handleComplexityChange(key, complexity)}
                        >
                          <span className="text-[14px] leading-[20px] font-semibold text-[#0D0C07]">
                            {t(`cookingStyle.complexity.${complexity}`)}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Cook for 2 days toggle */}
                  <div className="flex items-center py-2">
                    {/* iOS-style toggle */}
                    <button
                      onClick={() => handleCookFor2DaysToggle(key)}
                      className={`
                        relative w-[48px] h-[28px] border-[0.5px] rounded-[12px] transition-colors duration-300 ease-in-out mr-2
                        ${cookFor2Days ? 'bg-[#FFCB00] border-[#0D0C07]' : 'border-[#fff] bg-[#E5E5EA]'}
                      `}
                      role="switch"
                      aria-checked={cookFor2Days}
                    >
                      <span
                        className={`
                          absolute top-[2px] left-[2px] w-[23px] h-[23px] bg-white rounded-[10px] shadow-md
                          transition-transform duration-300 ease-in-out
                          ${cookFor2Days ? 'translate-x-[20px]' : 'translate-x-0'}
                        `}
                      />
                    </button>
                    <span 
                      className="text-[14px] leading-[24px] font-semibold text-[#0D0C07] cursor-pointer"
                      onClick={() => handleCookFor2DaysToggle(key)}
                    >
                      {t('cookingStyle.prepareFor2Days')}
                    </span>
                    {costHint && (
                      <span className="text-[13px] leading-[24px] font-semibold text-[rgba(13,12,7,0.55)] ml-1">
                        ({costHint})
                      </span>
                    )}
                    
                    {/* Info icon with tooltip */}
                    <div className="relative ml-1.5 inline-block">
                      <button
                        className="flex items-center justify-center w-4 h-4 text-[rgba(13,12,7,0.5)] md:hover:text-[#0D0C07] transition-colors group"
                        onClick={() => setVisibleTooltip(visibleTooltip === key ? null : key)}
                        onMouseEnter={() => window.innerWidth >= 768 && setVisibleTooltip(key)}
                        onMouseLeave={() => window.innerWidth >= 768 && setVisibleTooltip(null)}
                        aria-label={t('cookingStyle.tooltipAriaLabel', 'More information')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 16v-4"/>
                          <path d="M12 8h.01"/>
                        </svg>
                      </button>
                      
                      {/* Tooltip */}
                      {visibleTooltip === key && (
                        <div 
                          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-[200px] p-3 bg-[#0D0C07] text-white text-[13px] leading-[18px] rounded-[8px] shadow-lg z-50"
                          onClick={() => setVisibleTooltip(null)}
                        >
                          {t('cookingStyle.tooltip2Days', 'Cook once and enjoy for 2 days! Save time by not cooking every day.')}
                          {/* Tooltip arrow */}
                          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#0D0C07]" />
                        </div>
                      )}
                    </div>
                    
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Fixed Save Button */}
        <div
          className="
            fixed bottom-[16px] left-[20px] right-[20px]
            py-2 px-2 
            flex justify-center
            bg-[linear-gradient(135deg,_rgba(255,255,255,0.6)_0%,_rgba(255,255,255,0.8)_100%)]
            backdrop-blur-lg
            z-50
            max-w-5xl
            mx-auto
            rounded-[96px]
            border-[0.5px] border-[linear-gradient(145deg,_rgba(255,255,255,0.8)_0%,_rgba(255,255,255,1)_100%)]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
          " 
        >
          <div className="w-full max-w-5xl">
            <button
              className={`
                inline-flex justify-center items-center gap-1
                h-[48px] px-[24px] py-[14px]
                rounded-[32px]
                border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                bg-[#090]
                text-[#FFF] text-center
                text-[14px] font-medium leading-[28px]
                transition-colors
                w-full
                shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
                ${
                  isSaving
                    ? "cursor-not-allowed opacity-60 bg-[#090]"
                    : "cursor-pointer bg-[linear-gradient(200deg,_#090_0%,_#060_100%)] md:hover:border-[0.5px] md:hover:border-[#060] md:hover:bg-[#060]"
                }
              `}
              onClick={isSaving ? undefined : handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Spinner size="sm" color="#FFF" />
                  <span>{t('common:buttons.saving', 'Saving...')}</span>
                </>
              ) : (
                <span>{t('common:buttons.save', 'Save')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>, document.body
  );
};

export default CookingStyleDrawer;
